import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { CommissionOverview } from '@/lib/types';

/**
 * Everyone's commission progress for one period (`GET /commissions/overview`,
 * owner 2026-10-27): the Financial Analytics "Targets & commission" section.
 *
 * - `periodId === null` (no cycle chosen, or a custom date range) is `idle`:
 *   nothing is fetched and the panel renders nothing.
 * - A 403 is `forbidden`, not an error: the endpoint is `commissions.read`
 *   scope `all` only, and a reader without it simply does not get the section.
 * - `loading` is derived from a stamped key, so a change of period reads as
 *   loading rather than showing the previous period's rows for a frame.
 */
export type CommissionOverviewStatus = 'idle' | 'loading' | 'ready' | 'forbidden' | 'error';

export interface CommissionOverviewState {
  status: CommissionOverviewStatus;
  data: CommissionOverview | null;
  error: Error | null;
  reload: () => void;
}

interface Answer {
  key: string;
  status: 'ready' | 'forbidden' | 'error';
  data: CommissionOverview | null;
  error: Error | null;
}

function isForbidden(caught: unknown): boolean {
  return caught instanceof ApiError && (caught.status === 403 || caught.code === 'FORBIDDEN');
}

export function useCommissionOverview(periodId: number | null): CommissionOverviewState {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const key = periodId === null ? '' : `${periodId}|${reloadToken}`;

  useEffect(() => {
    if (periodId === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stamp = `${periodId}|${reloadToken}`;

    api
      .commissionOverview(periodId, signal)
      .then((data) => {
        if (signal.aborted) return;
        setAnswer({ key: stamp, status: 'ready', data, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        if (isForbidden(caught)) {
          setAnswer({ key: stamp, status: 'forbidden', data: null, error: null });
          return;
        }
        setAnswer({
          key: stamp,
          status: 'error',
          data: null,
          error: caught instanceof Error ? caught : new Error('Could not read commission targets.'),
        });
      });

    return () => controller.abort();
  }, [periodId, reloadToken]);

  if (key === '') return { status: 'idle', data: null, error: null, reload };
  const current = answer !== null && answer.key === key ? answer : null;
  if (current === null) return { status: 'loading', data: null, error: null, reload };
  return { status: current.status, data: current.data, error: current.error, reload };
}
